/**
 * Meals list route
 * Browse and search meals with pagination and filtering
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeals } from '@/hooks/useMeals';
import { MealList } from '@/components/meals/MealList';
import { MealSearch } from '@/components/meals/MealSearch';
import { MealTagFilter } from '@/components/meals/MealTagFilter';
import { requireAuth } from '@/lib/auth/routeGuards';

type MealsSearch = {
  page?: number;
};

export const Route = createFileRoute('/meals/')({
  beforeLoad: requireAuth,
  component: MealsListComponent,
  validateSearch: (search: Record<string, unknown>): MealsSearch => ({
    page: Number(search.page) || 1,
  }),
});

function MealsListComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Get user from route context - guaranteed non-null by requireAuth
  const { user } = Route.useRouteContext();
  const { page = 1 } = Route.useSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const limit = 20;

  const setPage = (newPage: number) => {
    navigate({ to: '/meals', search: { page: newPage } });
  };

  // Fetch meals with current filters
  const { data, isLoading, error } = useMeals({
    query: searchQuery || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    page,
    limit,
  });

  const meals = data?.meals || [];
  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('navigation.browseMeals')}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {t('home.browseMealsDescription')}
            </p>
          </div>

          {/* Organizer actions */}
          {user.isOrganizer && (
            <div className="flex gap-2">
              <Link
                to="/meals/enrich"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md shadow-sm transition-colors"
              >
                <span>✨</span>
                <span>{t('enrich.buttonLabel')}</span>
              </Link>
              <Link
                to="/meals/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-700 hover:bg-primary-800 text-white font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>{t('home.createMeal')}</span>
              </Link>
            </div>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MealSearch value={searchQuery} onChange={handleSearchChange} />
            <MealTagFilter selectedTags={selectedTags} onChange={handleTagsChange} />
          </div>

          {/* Active filters summary */}
          {(searchQuery || selectedTags.length > 0) && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {data?.total || 0} {data?.total !== 1 ? t('menu.meals') : t('menu.meal')} {t('common.found', 'trouvé(s)')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedTags([]);
                  setPage(1);
                }}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {t('meals.clearFilters')}
              </button>
            </div>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('meals.errorLoadingMeal')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error
                    ? error.message
                    : t('meals.deleteFailed')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Meal list */}
        <MealList meals={meals} isLoading={isLoading} />

        {/* Pagination */}
        {!isLoading && meals.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {page} {t('common.of', 'sur')} {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(page - 1)}
                disabled={!hasPreviousPage}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('common.previousPage')}
              </button>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={!hasNextPage}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('common.nextPage')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
